import { LOCAL_STORAGE_KEYS } from '@/constants/storage';
import { getAuthContext } from '@/context/global/globalAuthRef';
import { getLoadingContext } from '@/context/global/globalLoadingRef';
import axios, {
  AxiosError,
  AxiosResponse,
  InternalAxiosRequestConfig,
} from 'axios';
import { postReIssue } from './auth/postReIssue';

let isRefreshing = false;
let refreshSubscribers: ((token: string) => void)[] = [];

const subscribeTokenRefresh = (cb: (token: string) => void) => {
  refreshSubscribers.push(cb);
};

export const api = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL,
  timeout: 10000,
  withCredentials: true,
  headers: { 'Content-Type': 'application/json' },
});

api.interceptors.request.use(
  (config) => {
    // token이 필요 없으면 skipAuth 플래그 설정
    if ((config as any).skipAuth) {
      return config;
    }
    getLoadingContext()?.startLoading(); // 로딩 시작
    const token = localStorage.getItem(LOCAL_STORAGE_KEYS.ACCESS_TOKEN);
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    getLoadingContext()?.stopLoading(); // 실패 시 로딩 종료
    return Promise.reject(error);
  }
);

api.interceptors.response.use(
  (response) => {
    getLoadingContext()?.stopLoading();
    return response; // 서버에서 내려온 정상 응답(에러 포함) 그대로 반환
  },
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & {
      _retry?: boolean;
    };
    getLoadingContext()?.stopLoading(); // 실패 시 로딩 종료
    const authContext = getAuthContext();
    // 403 처리: 권한 없음
    if (error.response?.status === 403 && authContext) {
      authContext.logout();
      return Promise.reject(error);
    }
    // 401 처리: 토큰 만료
    if (
      error.response?.status === 401 &&
      authContext &&
      !originalRequest._retry
    ) {
      originalRequest._retry = true;
      // 이미 재발급 중이면 대기
      if (isRefreshing) {
        return new Promise((resolve) => {
          subscribeTokenRefresh((token: string) => {
            originalRequest.headers.Authorization = `Bearer ${token}`;
            resolve(api(originalRequest));
          });
        });
      }

      isRefreshing = true;
      try {
        const res = await postReIssue();
        if (res.isSuccess) {
          const { accessToken: newAccessToken } = res.data;
          if (newAccessToken) {
            localStorage.setItem(
              LOCAL_STORAGE_KEYS.ACCESS_TOKEN,
              newAccessToken
            );
          }
          originalRequest.headers.Authorization = `Bearer ${newAccessToken}`; // 새 토큰 저장
          return api(originalRequest);
        } else {
          console.log('여기');
          authContext.logout();
        }
      } catch (refreshError) {
        authContext.logout();
      } finally {
        isRefreshing = false;
      }
    }

    // 네트워크/타임아웃/500 에러 동일하게 모달 처리
    if (!error.response || error.response.status >= 500) {
      // showGlobalErrorAlert(
      //   i18next.t('failedToconnectTotheServerPleaseTryAgainLater')
      // );
    }

    return Promise.reject(error);
  }
);
